import { addDays, addYears, addSeconds } from 'date-fns/';

import store from 'lib/store';

export default class BaseAuth {
  constructor({ baseUrl, clientId, credentials, deviceId, isDev }) {
    this.baseUrl = baseUrl;
    this.clientId = clientId;
    this.credentials = credentials;
    this.deviceId = deviceId;
    this.isDev = isDev;
  }
  agreeToTerms = async () => {
    const { accessToken, idToken, refreshToken, ...data } = await this.request(
      'agreeToTerms',
      {
        // manually include this idToken here, which presumably has permissions
        // for the `agreeToTerms` action
        idToken: await this.getIdToken(),
      },
    );
    await this.handleTokens({
      accessToken,
      idToken,
      refreshToken,
    });
    return data;
  };
  request = async (action, body = {}) => {
    try {
      const customBody = {
        clientId: this.clientId,
        ...body,
      };
      if (this.deviceId) {
        customBody.deviceId = this.deviceId;
      }
      const url = `${this.baseUrl}/auth/${action}`;
      const options = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: this.credentials,
        body: JSON.stringify(customBody),
      };
      if (this.isDev) {
        // eslint-disable-next-line no-console
        console.log('AUTH API REQUEST:', { url, options });
      }
      const response = await fetch(url, options);
      const json = await response.json();
      const data = {
        err: !response.ok,
        ...json,
      };
      if (this.isDev) {
        // eslint-disable-next-line no-console
        console.log('AUTH API RESPONSE:', data);
      }
      return data;
    } catch (e) {
      if (this.isDev) {
        // eslint-disable-next-line no-console
        console.log('AUTH API ERROR:', e);
      }
      return {
        err: true,
        message:
          'There was an error processing your request, please try again.',
      };
    }
  };
  deleteCredentials = async () => {
    await this.deleteAccessToken();
    await this.deleteIdToken();
    await this.deleteRefreshToken();
    await this.deleteShortLivedToken();
  };
  deleteAccessToken = async () => store.delete('accessToken');
  deleteIdToken = async () => store.delete('idToken');
  deleteRefreshToken = async () => store.delete('refreshToken');
  deleteShortLivedToken = async () => store.delete('shortLivedToken');
  forgotPassword = async email => {
    const { accessToken, idToken, refreshToken, ...data } = await this.request(
      'forgotPassword',
      {
        // manually include this idToken here, which presumably has permissions
        // for the `forgotPassword` action
        idToken: await this.getIdToken(),
        email,
      },
    );
    await this.handleTokens({
      accessToken,
      idToken,
      refreshToken,
    });
    return data;
  };
  getCredentials = async () => {
    const accessToken = await store.get('accessToken');
    const idToken = await store.get('idToken');
    const refreshToken = await store.get('refreshToken');
    const shortLivedToken = await store.get('shortLivedToken');
    return { accessToken, idToken, refreshToken, shortLivedToken };
  };
  getAccessToken = async () => store.get('accessToken');
  getIdToken = async () => store.get('idToken');
  getRefreshToken = async () => store.get('refreshToken');
  getShortLivedToken = async () => {
    let shortLivedToken = await store.get('shortLivedToken');
    if (!shortLivedToken) {
      shortLivedToken = await this.fetchShortLivedToken();
    }
    return shortLivedToken;
  };
  handleTokens = async ({ accessToken, idToken, refreshToken, shortLivedToken }) => {
    if (accessToken && refreshToken) {
      await this.setCredentials(accessToken, refreshToken);
    } else if (idToken) {
      await this.setIdToken(idToken);
    }
    if (shortLivedToken) {
      await this.setShortLivedToken(shortLivedToken);
    }
  };
  isSignedIn = async () => {
    const { accessToken, refreshToken } = await this.getCredentials();
    return !!accessToken && !!refreshToken;
  };
  isRefreshIn = async () => {
    const { refreshToken } = await this.getCredentials();
    return refreshToken;
  };
  isShortLivedToken = async () => {
    const { shortLivedToken } = await this.getCredentials();
    return shortLivedToken;
  };
  login = async (email, password) => {
    const { accessToken, idToken, refreshToken, ...data } = await this.request(
      'login',
      {
        email,
        password,
      },
    );
    await this.handleTokens({ accessToken, idToken, refreshToken });
    return data;
  };
  // eslint-disable-next-line no-unused-vars,no-console
  loginSocial = provider => console.error('Method must be overridden!');
  logout = async () => {
    const accessToken = await this.getAccessToken();
    const idToken = await this.getIdToken();
    const refreshToken = await this.getRefreshToken();
    await this.deleteCredentials();
    return this.request('logout', {
      accessToken,
      idToken,
      refreshToken,
    });
  };
  refreshToken = async () => {
    const refreshToken = await this.getRefreshToken();
    if (refreshToken) {
      const resp = await this.request('refreshToken', { refreshToken });
      if (!resp.err && resp.accessToken) {
        const { accessToken, accessTokenMaxAge } = resp;
        if (accessToken) {
          await this.setAccessToken(accessToken, accessTokenMaxAge);
        }
        return accessToken;
      }
      return this.logout();
    }
    return null;
  };
  register = async (email, password, redirect) => this.request('register', {
    email,
    password,
    redirect,
  });
  resetPassword = async password => {
    const { accessToken, idToken, refreshToken, ...data } = await this.request(
      'resetPassword',
      {
        // manually include this idToken here, which presumably has permissions
        // for the `resetPassword` action
        idToken: await this.getIdToken(),
        password,
      },
    );
    await this.handleTokens({
      accessToken,
      idToken,
      refreshToken,
    });
    return data;
  };
  setAccessToken = async (accessToken, expiry = 3600) => {
    store.save('accessToken', accessToken, addSeconds(new Date(), expiry));
  };
  setCredentials = async (accessToken, refreshToken) => {
    await this.deleteIdToken();
    await this.setAccessToken(accessToken);
    await this.setRefreshToken(refreshToken);
  };
  setIdToken = async idToken => store.save('idToken', idToken, addDays(new Date(), 1));
  setRefreshToken = async refreshToken => store.save('refreshToken', refreshToken, addYears(new Date(), 1));
  setShortLivedToken = async (shortLivedToken, expiry = 600) => {
    store.save('shortLivedToken', shortLivedToken, addSeconds(new Date(), expiry));
  };
  fetchShortLivedToken = async () => {
    const url = `${process.env.MS_API_GATEWAY_URL}/api/iam/v1/auth/anonymousToken`;
    const body = {
      deviceId: '',
    };

    try {
      body.deviceId = localStorage.getItem('rs_device_id');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch the deviceId', e.message);
    }
    const options = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: this.credentials,
      body: JSON.stringify(body),
    };
    if (this.isDev) {
      // eslint-disable-next-line no-console
      console.log('FETCH SHORT-LIVED TOKEN REQUEST:', { url, options });
    }
    try {
      const response = await fetch(url, options);
      const json = await response.json();
      if (response.ok) {
        const { accessToken, accessTokenMaxAge } = json;
        if (accessToken) {
          await this.setShortLivedToken(accessToken, accessTokenMaxAge);
        }
        return json;
      }
      return null;
    } catch (e) {
      if (this.isDev) {
        // eslint-disable-next-line no-console
        console.log('FETCH SHORT-LIVED TOKEN ERROR:', e);
      }
      return null;
    }
  };
}
